/**
 * Returns the current vertical scroll offset of the page.
 *
 * When a modal is open, the body is scroll-locked (e.g. position: fixed with a
 * negative `top`), which resets `window.scrollY` to 0. The fallbacks below
 * recover the real scroll position in that case.
 */
export const getScrollOffset = (): number => {
  // Server-side rendering: no window, so no scroll.
  if (typeof window === 'undefined') {
    return 0;
  }

  // Normal case: the page is scrolled and not locked.
  if (window.scrollY > 0) {
    return window.scrollY;
  }

  const { body } = document;
  const bodyTop = body?.style?.top;

  // Lock strategy 1: the lock sets an inline negative `top` (e.g. "-250px")
  // equal to the scroll position at the time of locking.
  if (bodyTop) {
    const parsedTop = Number.parseFloat(bodyTop);
    if (!Number.isNaN(parsedTop) && parsedTop < 0) {
      return Math.abs(parsedTop);
    }
  }

  // Lock strategy 2: the body is fixed-positioned by some other mechanism;
  // use its bounding rect offset in the same way.
  const bodyPosition = body ? window.getComputedStyle(body).position : '';
  if (bodyPosition === 'fixed') {
    const bodyRectTop = body.getBoundingClientRect().top;
    if (bodyRectTop < 0) {
      return Math.abs(bodyRectTop);
    }
  }

  // Final fallback: root element's scrollTop (some browsers use it instead of
  // window.scrollY), or 0 if the page is not scrolled.
  return document.documentElement.scrollTop || 0;
};

export default getScrollOffset;
